import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  Assignee,
  Category,
  CreateTicketBody,
  Priority,
  ResponseTemplate,
  SupportStage,
  SupportStats,
  TemplatePreview,
  TicketDetail,
  TicketListResponse,
} from './types'

const KEY = ['support'] as const
const STAGES_KEY = ['support', 'stages'] as const
const TEMPLATES_KEY = ['support', 'templates'] as const

export interface TicketListFilters {
  stage_id?: string
  category?: Category
  assigned_to?: Assignee | 'unassigned'
  priority?: Priority
  partner_id?: number
  is_archived?: boolean
  sla_breached?: boolean
  so_ref?: string
  search?: string
  page?: number
  limit?: number
}

export function useTicketList(filters: TicketListFilters = {}) {
  const params = new URLSearchParams()
  if (filters.stage_id) params.set('stage_id', filters.stage_id)
  if (filters.category) params.set('category', filters.category)
  if (filters.assigned_to) params.set('assigned_to', filters.assigned_to)
  if (filters.priority) params.set('priority', filters.priority)
  if (filters.partner_id != null) params.set('partner_id', String(filters.partner_id))
  if (filters.is_archived) params.set('is_archived', 'true')
  if (filters.sla_breached) params.set('sla_breached', 'true')
  if (filters.so_ref) params.set('so_ref', filters.so_ref)
  if (filters.search) params.set('search', filters.search)
  params.set('page', String(filters.page ?? 1))
  params.set('limit', String(filters.limit ?? 200))
  const qs = params.toString()

  return useQuery({
    queryKey: [...KEY, 'list', filters],
    queryFn: () => jsonFetch<TicketListResponse>(`/api/support/tickets?${qs}`),
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function useSupportStats() {
  return useQuery({
    queryKey: [...KEY, 'stats'],
    queryFn: () => jsonFetch<SupportStats>('/api/support/tickets/stats'),
    staleTime: 15_000,
    // Mirror the list query so the KPI strip auto-refreshes while the board
    // is open (counts + SLA-breached track the current time without a manual
    // reload). Previously stats had no polling, so the numbers stayed frozen.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function useTicketDetail(ticketId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', ticketId],
    queryFn: () => jsonFetch<TicketDetail>(`/api/support/tickets/${ticketId}`),
    enabled: !!ticketId,
    staleTime: 5_000,
  })
}

export function useStages() {
  return useQuery({
    queryKey: [...STAGES_KEY],
    queryFn: () => jsonFetch<{ items: SupportStage[] }>('/api/support/stages'),
    staleTime: 60_000,
  })
}

export function useTemplates(category?: Category) {
  const qs = category ? `?category=${category}` : ''
  return useQuery({
    queryKey: [...TEMPLATES_KEY, category ?? 'all'],
    queryFn: () =>
      jsonFetch<{ items: ResponseTemplate[] }>(`/api/support/templates${qs}`),
    staleTime: 60_000,
  })
}

// ─── Mutations ──────────────────────────────────────────────────────

export function useCreateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateTicketBody) =>
      jsonFetch<TicketDetail>('/api/support/tickets', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function usePatchTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; edits: Record<string, unknown> }) =>
      jsonFetch<TicketDetail>(`/api/support/tickets/${vars.id}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useMoveStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; targetStageId: string; note?: string }) =>
      jsonFetch(`/api/support/tickets/${vars.id}/move-stage`, {
        method: 'POST',
        body: { target_stage_id: vars.targetStageId, note: vars.note },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useArchiveTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; reason?: string }) =>
      jsonFetch(`/api/support/tickets/${vars.id}/archive`, {
        method: 'POST',
        body: { reason: vars.reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUnarchiveTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch(`/api/support/tickets/${id}/unarchive`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useSendResponse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      id: string
      target_email: string
      template_id?: string
      subject?: string
      body?: string
    }) =>
      jsonFetch(`/api/support/tickets/${vars.id}/send-response`, {
        method: 'POST',
        body: {
          target_email: vars.target_email,
          template_id: vars.template_id,
          subject: vars.subject,
          body: vars.body,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useMarkResponded() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; note?: string }) =>
      jsonFetch(`/api/support/tickets/${vars.id}/mark-responded`, {
        method: 'POST',
        body: { note: vars.note },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useRefreshOdoo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<{ context: unknown }>(`/api/support/tickets/${id}/refresh-odoo`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function usePreviewTemplate() {
  return useMutation({
    mutationFn: (vars: { templateId: string; ticketId: string }) =>
      jsonFetch<TemplatePreview>(
        `/api/support/templates/${vars.templateId}/preview?ticket_id=${vars.ticketId}`,
        { method: 'POST' },
      ),
  })
}

// ─── Stage management ───────────────────────────────────────────────

export function useReorderStages() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (order: { id: string; sequence: number }[]) =>
      jsonFetch<{ items: SupportStage[] }>('/api/support/stages/reorder', {
        method: 'POST',
        body: { order },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAGES_KEY }),
  })
}

export function useCreateStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      name: string
      color?: string
      is_terminal?: boolean
      is_archive?: boolean
    }) =>
      jsonFetch<SupportStage>('/api/support/stages', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAGES_KEY }),
  })
}

export function usePatchStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; edits: Record<string, unknown> }) =>
      jsonFetch<SupportStage>(`/api/support/stages/${vars.id}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAGES_KEY }),
  })
}

export function useDeleteStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; migrateToStageId?: string }) => {
      const qs = vars.migrateToStageId
        ? `?migrate_to_stage_id=${vars.migrateToStageId}`
        : ''
      return jsonFetch(`/api/support/stages/${vars.id}${qs}`, { method: 'DELETE' })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support'] }),
  })
}
